/* eslint-disable react/no-did-update-set-state */
import { ChoiceTree } from '@alkem/react-ui-choicetree';
import { ClickOutside } from '@alkem/react-ui-click-outside';
import { Tag } from '@alkem/react-ui-inputs';
import { Spinner } from '@alkem/react-ui-spinner';
import { Tooltip } from '@alkem/react-ui-tooltip';
import classNames from 'classnames';
import { get, noop } from 'lodash';
import PropTypes from 'prop-types';
import { createRef, PureComponent } from 'react';
import './select.scss';
import {
  getTreeNode,
  getTreePath,
  onArrowDown,
  onArrowLeft,
  onArrowRight,
  onArrowUp,
  openAlongPath,
} from './tree-navigation';

class Select extends PureComponent {
  static demoProps = [
    {
      id: 'demo_select_1',
      placeholder: 'Choose an option',
      options: [
        { label: 'Choice 1', value: 1 },
        { label: 'Choice 2', value: 2 },
      ],
      multiple: false,
    },
    {
      id: 'demo_select_2',
      placeholder: 'Choose an option',
      options: [
        { label: 'Choice 1', value: 1 },
        { label: 'Choice 2', value: 2 },
      ],
      values: [{ label: 'Choice 1', value: 1 }],
      disabled: true,
    },
    {
      id: 'demo_select_2',
      placeholder: 'Choose an option',
      options: [
        { label: 'Choice 1', value: 1 },
        { label: 'Choice 2', value: 2 },
      ],
      disabled: true,
    },
    {
      id: 'demo_select_3',
      placeholder: 'Choose an option',
      options: [
        { label: 'Choice 1', value: 1 },
        { label: 'Choice 2', value: 2 },
      ],
      multiple: true,
    },
    {
      id: 'demo_select_4',
      placeholder: 'Choose an option',
      options: [
        { label: 'Choice 1', value: 1 },
        { label: 'Choice 2', value: 2 },
      ],
      values: [
        { label: 'Choice 1', value: 1 },
        { label: 'Choice 2', value: 2 },
      ],
      disabled: true,
      multiple: true,
    },
    {
      id: 'Select_demo_select_5',
      placeholder: 'Choose an option',
      options: [
        {
          key: 15050,
          label: 'Select_demo_select_5_option1',
          value: {},
          children: [
            {
              key: 115050,
              label: 'Select_demo_select_5_child1',
              children: [
                { key: 1115050, label: 'Select_demo_select_5_child11' },
              ],
            },
            {
              key: 125050,
              label: 'Select_demo_select_5_child2',
              children: [
                { key: 1215050, label: 'Select_demo_select_5_child21' },
                { key: 1225050, label: 'Select_demo_select_5_child22' },
              ],
            },
          ],
        },
        { key: 2, label: 'option2', value: {} },
        { key: 5, label: 'option5', value: {} },
        { key: 6, label: 'option5', value: {} },
      ],
      isTree: true,
      inputable: true,
    },
  ];

  static demoHandlers = {
    onValueAdd: {
      prop: 'values',
      handler: (item) => [item],
    },
    onValueDelete: {
      prop: 'values',
      handler: () => [],
    },
  };

  static LOTS_OF_LEAVES = 50;

  static defaultProps = {
    placeholder: 'Select…',
    label: undefined,
    labelSize: 4,
    disabled: false,
    multiple: false,
    inputable: false,
    options: [],
    values: [],
    extraOptions: {
      hideToggleButton: false,
    },
    isTree: false,
    valueEqualsTreeItem: (x, y) => x === y,
    displaySpinner: false,
    scrollIntoView: false,
  };

  constructor(props) {
    super(props);

    this.onClick = this.onClick.bind(this);
    this.onSelectChange = this.onSelectChange.bind(this);
    this.filterOptions = this.filterOptions.bind(this);
    this.onInputChange = this.onInputChange.bind(this);
    this.handleKeyEvent = this.handleKeyEvent.bind(this);
    this.deleteTag = this.deleteTag.bind(this);
    this.onBlur = this.onBlur.bind(this);
    this.onFocus = this.onFocus.bind(this);
    this.setSelectedPath = this.setSelectedPath.bind(this);

    this.defaultFocusPath = [];

    this.state = {
      isOpen: false,
      inputValue: '',
      options: this.props.options,
      delegateSearch: this.props.delegateSearch
        ? this.props.delegateSearch
        : this.filterOptions,
      focusPath: this.defaultFocusPath,
      selectedPath: [],
      tooltip: false,
    };

    this.inputRef = createRef();
    this.valueRef = createRef();
    this.tooltipRef = createRef();
    this.tooltipTimer = null;

    if (props.values && props.values.length > 0) {
      const selectedPath = getTreePath(
        { children: props.options },
        props.values[0],
        props.valueEqualsTreeItem
      );
      this.state.selectedPath = selectedPath || [];
      const openedTree = openAlongPath(
        { children: this.state.options },
        this.state.selectedPath
      );
      this.state.options =
        openedTree !== null ? openedTree.children : this.state.options;
    }
  }

  componentDidMount() {
    this.resetInputValue(this.props);
  }

  componentDidUpdate(prevProps, prevState) {
    // If inputable and not multiple and we have a selected value, we display the label of the value in the input.
    if (
      (this.state.isOpen !== prevState.isOpen && !this.state.isOpen) ||
      prevProps.values !== this.props.values
    ) {
      this.resetInputValue(this.props);
    }

    if (!this.props.isTree && this.props.options !== prevProps.options) {
      this.setState({ options: this.props.options });
    }

    if (
      this.props.isTree &&
      (this.props.values !== prevProps.values ||
        this.props.options !== prevProps.options)
    ) {
      if (this.props.values && this.props.values.length > 0) {
        this.setSelectedPath(this.props);
      } else {
        this.setState({ selectedPath: [], options: this.props.options });
      }
    }

    if (!prevState.tooltip && this.state.tooltip && this.tooltipRef.current) {
      Tooltip.show(this.tooltipRef.current);
    }
  }

  componentWillUnmount() {
    clearTimeout(this.tooltipTimer);
  }

  onClick() {
    const { isOpen } = this.state;
    if (this.props.disabled) {
      return;
    }

    if (isOpen) {
      this.setState({ isOpen: false, focusPath: this.defaultFocusPath });
    } else {
      this.setState({ isOpen: true });
    }

    if (this.inputRef.current) {
      this.inputRef.current.focus();
    }
  }

  onSelectChange(item, path) {
    // Call onValueAdd then close dropdown
    const { onValueAdd, isTree } = this.props;
    // For the case of a choice tree
    if (path && isTree) {
      this.setState({ selectedPath: path });
    }
    onValueAdd(item);
    this.handleClickOutside();
  }

  onDelete = (event) => {
    const { disabled } = this.props;
    if (disabled) {
      return;
    }
    // Global delete (simple select case)
    const item = this.props.values[0];
    this.props.onValueDelete(item, event);
    this.setState({ isOpen: false });
  };

  onValueDelete = (index) => () => {
    const { onValueDelete } = this.props;
    onValueDelete(index);
  };

  onInputChange(event) {
    if (!this.focused) {
      return;
    }
    const { isOpen } = this.state;

    if (!isOpen) {
      this.setState({ isOpen: true });
    }
    this.setState({ inputValue: event.target.value });
    this.state.delegateSearch(event.target.value);
  }

  onBlur() {
    this.focused = false;
  }

  onFocus(event) {
    if (this.props.onFocus) {
      this.props.onFocus(event);
    }
    return this.handleKeyEvent(event);
  }

  getValueLabel(value, props) {
    const { extraOptions } = props || this.props;
    let displayCode = false;
    let displayLabelWithCode = false;
    if (extraOptions && extraOptions.display && extraOptions.display.values) {
      displayCode = extraOptions.display.values === 'code';
      displayLabelWithCode = extraOptions.display.values === 'labelWithCode';
    }
    // At first display, values[0] is an entity directly, with a `code`
    // attribute. When selecting a new value however, values[0] has the entity
    // in an `item` attribute.
    if (value) {
      const code = value.code || (value.item && value.item.code);
      const label = value.label || (value.item && value.item.label);
      return (
        (displayCode && code) ||
        (displayLabelWithCode && `${label} (${code})`) ||
        label ||
        ''
      );
    }
    return '';
  }

  getNumberOfLeaves(options) {
    return options.reduce((acc, o) => {
      if (acc > Select.LOTS_OF_LEAVES) {
        return acc;
      }
      const children = get(o, 'children') || [];
      if (children.length === 0) {
        return acc + 1;
      }
      return acc + this.getNumberOfLeaves(children);
    }, 0);
  }

  setSelectedPath(props) {
    if (props.isTree && props.values && props.values.length > 0) {
      const selectedPath =
        getTreePath(
          { children: props.options },
          props.values[0],
          props.valueEqualsTreeItem
        ) || [];
      const openedTree = openAlongPath(
        { children: props.options },
        selectedPath
      );
      const options = openedTree !== null ? openedTree.children : props.options;
      this.setState({ selectedPath, options });
    }
  }

  handleClickOutside = () => {
    if (this.state.isOpen) {
      this.resetInputValue();
      this.setState({ isOpen: false, focusPath: this.defaultFocusPath });
    }
  };

  // ---------------------------------------------------------------------------
  // Actions
  // ---------------------------------------------------------------------------

  filterOptions(currentValue) {
    const { options } = this.props;
    const filteredOptions = this.filterTree(
      { children: options },
      currentValue
    );
    this.setState({ options: get(filteredOptions, 'children', []) });
  }

  normalizeString(str) {
    if (typeof str !== 'string') {
      return str;
    }
    if (str.normalize) {
      return str.normalize().toLowerCase();
    }
    return str.toLowerCase();
  }

  filterTree(node, value) {
    if (Array.isArray(node.children) && node.children.length > 0) {
      const filteredChildren = node.children
        .map((child) => this.filterTree(child, value))
        .filter(Boolean);
      if (filteredChildren.length > 0) {
        const filteredNode = { ...node };
        filteredNode.children = filteredChildren;
        return filteredNode;
      }
    }
    const label = this.getValueLabel(node);
    const normalized = this.normalizeString(label);
    if (normalized && normalized.includes(this.normalizeString(value))) {
      return node;
    }
    return null;
  }

  deleteTag(key) {
    const item = this.props.values.find((value) => value.value === key);
    this.props.onValueDelete(item);
  }

  isSelectedValueInputable(props) {
    const { values, multiple, inputable } = props || this.props;
    return (
      inputable && !multiple && values && values.length === 1 && !!values[0]
    );
  }

  resetInputValue(newProps) {
    const props = newProps || this.props;

    if (this.isSelectedValueInputable(props)) {
      const value = props.values[0];
      this.setState({ inputValue: this.getValueLabel(value) });
      this.setSelectedPath(props);
    } else {
      this.setState({ inputValue: '' });
    }
    if (props.delegateSearch) {
      this.setState({ options: [] });
    } else {
      this.setState({ options: props.options }); // Refill.
    }
  }

  shouldFlattenOptions() {
    const { values, options, extraOptions } = this.props;
    const { inputValue } = this.state;
    return (
      !!inputValue &&
      !!get(extraOptions, 'selectOnlyLeaves') &&
      !values.find((v) => this.getValueLabel(v) === inputValue) &&
      this.getNumberOfLeaves(options) <= Select.LOTS_OF_LEAVES // Too many will result in slow rendering.
    );
  }

  handleKeyEvent(event) {
    const { isTree } = this.props;
    this.focused = true;
    if (event.type === 'mouseover') {
      // Remove focus
      this.setState({ focusPath: this.defaultFocusPath });
    }
    const { focusPath, options, isOpen } = this.state;
    let nextState;

    // NB: stopping propagation here on event focus causes caret position error on Firefox only
    if (event.type !== 'focus') {
      event.stopPropagation();
    }
    if (options.length === 0) {
      return;
    }

    switch (event.key) {
      case 'ArrowDown':
        if (!isOpen) {
          this.onClick();
        }
        event.preventDefault();
        nextState = onArrowDown(
          { children: options },
          focusPath,
          this.shouldFlattenOptions()
        );
        if (nextState) {
          this.setState(nextState);
        }
        break;
      case 'ArrowUp':
        if (!isOpen) {
          this.onClick();
        }
        event.preventDefault();
        nextState = onArrowUp(
          { children: options },
          focusPath,
          this.shouldFlattenOptions()
        );
        if (nextState) {
          this.setState(nextState);
        }
        break;
      case 'ArrowRight':
        if (!isOpen) {
          this.onClick();
        }
        if (!isTree) {
          break;
        }
        // Since we are navigating in the tree, we don't want to see the cursor move in the input
        if (this.state.focusPath.length > 0) {
          event.preventDefault();
        }
        nextState = onArrowRight(options, focusPath);
        if (nextState) {
          this.setState(nextState);
        }
        break;
      case 'ArrowLeft':
        if (!isOpen) {
          this.onClick();
        }
        if (!isTree) {
          break;
        }
        // Since we are navigating in the tree, we don't want to see the cursor move in the input
        if (this.state.focusPath.length > 0) {
          event.preventDefault();
        }
        nextState = onArrowLeft(options, focusPath);
        if (nextState) {
          this.setState(nextState);
        }
        break;
      case 'Escape':
        this.handleClickOutside();
        break;
      case 'Tab':
        this.handleClickOutside();
        break;
      case 'Enter':
        if (!isOpen) {
          break;
        }
        nextState = onArrowRight(options, focusPath);
        if (nextState) {
          this.setState(nextState);
        } else if (focusPath !== null && focusPath.length > 0) {
          this.onSelectChange(
            getTreeNode(focusPath, { children: options }),
            focusPath
          );
        }
        break;
      default:
        break;
    }
  }

  // ---------------------------------------------------------------------------
  // Rendering
  // ---------------------------------------------------------------------------

  renderSpinner() {
    const { displaySpinner } = this.props;
    if (!displaySpinner) {
      return null;
    }
    return (
      <div className="Select__spinner">
        <Spinner small />
      </div>
    );
  }

  renderDeleteSelectionButton() {
    const { values, multiple, displaySpinner, disabled, onValueDelete } =
      this.props;

    if (
      !onValueDelete ||
      !values ||
      disabled ||
      displaySpinner ||
      multiple ||
      values.length === 0
    ) {
      return null;
    }

    return (
      <div
        className="Select__deleteicon mdi mdi-close"
        onClick={this.onDelete}
      />
    );
  }

  renderToggleButton() {
    const {
      values,
      disabled,
      extraOptions,
      multiple,
      displaySpinner,
      onValueDelete,
    } = this.props;
    const { isOpen } = this.state;

    if (
      extraOptions?.hideToggleButton ||
      displaySpinner ||
      disabled ||
      (onValueDelete && values && values.length !== 0 && !multiple)
    ) {
      return null;
    }

    const classes = {
      Select__openicon: true,
      mdi: true,
      'mdi-magnify': extraOptions.searchIcon,
      'mdi-chevron-down': !isOpen && !extraOptions.searchIcon,
      'mdi-chevron-up': isOpen && !extraOptions.searchIcon,
    };
    return <div className={classNames(classes)} />;
  }

  renderLabel(htmlId) {
    const { label, labelSize } = this.props;
    if (!label) {
      return null;
    }

    return (
      <label
        htmlFor={htmlId}
        className={`InputField__label col-xs-${labelSize}`}
      >
        {label}
      </label>
    );
  }

  renderInput() {
    const {
      autoFocus,
      values,
      placeholder,
      disabled,
      inputable,
      multiple,
      id,
    } = this.props;

    const inputBoxClasses = {
      'Select__inputbox--content': true,
      Select__placeholder: !values,
    };

    const inputClasses = {
      Select__input: true,
      'Select__input--disabled': !!disabled,
    };

    const showInput =
      inputable &&
      (multiple ||
        !values ||
        values.length === 0 ||
        this.isSelectedValueInputable()) &&
      !(multiple && disabled);
    // Totally non-ideal workflow: basically, we have here:
    // if (inputable) <input> (renderInput() methode)
    // else (renderValues() method)
    //   if (some value(s) selected) <div Select__value>
    //   else <div Select__placeholder>
    // Hence two different elements and three different classes with as much
    // different appearances and behaviours while a single display would be
    // desired.
    return (
      <div className="Select__inputframe">
        {multiple ? this.renderValues() : null}
        <div
          className={classNames(inputBoxClasses)}
          onClick={values || multiple ? this.onClick : null}
          onMouseEnter={() => {
            const target = this.inputRef.current || this.valueRef.current;
            if (target && target.scrollWidth > target.offsetWidth) {
              this.tooltipTimer = setTimeout(() => {
                this.setState((state) =>
                  !state.tooltip ? { ...state, tooltip: true } : state
                );
              }, 200);
            }
          }}
          onMouseLeave={() => {
            clearTimeout(this.tooltipTimer);
            this.setState((state) =>
              state.tooltip ? { ...state, tooltip: false } : state
            );
          }}
          ref={this.tooltipRef}
          data-for={`${id}-tooltip`}
          data-tip
        >
          {showInput ? (
            <>
              <input
                type="text"
                ref={this.inputRef}
                className={classNames(inputClasses)}
                onChange={this.onInputChange}
                onFocus={this.onFocus}
                onBlur={this.onBlur}
                placeholder={disabled ? '' : placeholder}
                value={this.state.inputValue}
                disabled={disabled}
                autoComplete="off"
                autoFocus={autoFocus}
              />
              {this.state.tooltip && (
                <Tooltip id={`${id}-tooltip`}>{this.state.inputValue}</Tooltip>
              )}
            </>
          ) : null}
          {!multiple && !showInput ? this.renderValues() : null}
        </div>
      </div>
    );
  }

  renderValues() {
    // Render selected values, handling different displays with respect
    // to props: multiple, disabled, inputable
    const { values, disabled, placeholder, multiple, inputable, id } =
      this.props;
    if (multiple) {
      if (
        placeholder &&
        !inputable &&
        (!values || (values && values.length === 0))
      ) {
        return (
          <div className="Select__inputbox--placeholder">{placeholder}</div>
        );
      }

      return values.map((value, index) => {
        return (
          <Tag
            testid={`${this.props.id}-${value.item}`}
            className="TagsInput__tag Select__tag"
            key={value.label}
            value={value.item}
            label={value.label}
            onDelete={this.onValueDelete(index)}
            onClick={noop}
            readOnly={disabled}
          />
        );
      });
    } else if (values && values.length === 1 && !!values[0]) {
      const valueLabel = this.getValueLabel(values[0]);
      return (
        <>
          <div className="Select__value" ref={this.valueRef}>
            {valueLabel}
          </div>
          {this.state.tooltip && (
            <Tooltip id={`${id}-tooltip`}>{valueLabel}</Tooltip>
          )}
        </>
      );
    } else if (!disabled) {
      return <div className="Select__placeholder">{placeholder}</div>;
    }
    return null;
  }

  renderChoiceTree() {
    const { id, isTree, values, extraOptions, multiple, scrollIntoView } =
      this.props;
    const { isOpen, options, focusPath, inputValue } = this.state;
    const choicelistHtmlId = `SelectChoiceTree-${id}`;
    if (!isOpen || !options.length) {
      return null;
    }
    return (
      <ChoiceTree
        id={choicelistHtmlId}
        options={options}
        onSelect={this.onSelectChange}
        selectedValues={values}
        focusPath={focusPath}
        selectedPath={isTree && !multiple ? this.state.selectedPath : []}
        extraOptions={extraOptions}
        flatten={this.shouldFlattenOptions()}
        matchText={inputValue}
        scrollIntoView={scrollIntoView}
      />
    );
  }

  render() {
    const { id, values, label, labelSize, disabled, multiple } = this.props;
    const selectHtmlId = `Select-${id}`;

    const inputWrapperClasses = {
      InputField_input: true,
      'col-xs-12': !label,
      [`col-xs-${12 - labelSize}`]: !!label,
    };

    const inputClasses = {
      Select__inputbox: true,
      'Select__inputbox--disabled': !!disabled,
      'Select__inputbox--multiple': multiple,
    };

    return (
      <ClickOutside
        className="Select row"
        onClickOutside={this.handleClickOutside}
      >
        {this.renderLabel(selectHtmlId)}
        <div
          className={classNames(inputWrapperClasses)}
          onKeyDown={this.handleKeyEvent}
          onMouseOver={this.handleKeyEvent}
        >
          <div style={{ position: 'relative' }}>
            <div
              className={classNames(inputClasses)}
              id={selectHtmlId}
              onClick={values.length === 0 || multiple ? this.onClick : null}
            >
              {this.renderInput()}
              {this.renderDeleteSelectionButton()}
              {this.renderToggleButton()}
              {this.renderSpinner()}
            </div>
            {this.renderChoiceTree()}
          </div>
        </div>
      </ClickOutside>
    );
  }
}

Select.propTypes = {
  id: PropTypes.string.isRequired,
  placeholder: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  options: PropTypes.array.isRequired,
  onValueAdd: PropTypes.func.isRequired,
  onValueDelete: PropTypes.func,
  // Set delegateSearch to control the search method
  delegateSearch: PropTypes.func,
  values: PropTypes.array,
  // Set multiple to true to authorize multiple selections
  multiple: PropTypes.bool,
  // Set inputable to true to make the select searchable
  inputable: PropTypes.bool,
  label: PropTypes.string,
  labelSize: PropTypes.number,
  disabled: PropTypes.bool,
  // Extra options
  extraOptions: PropTypes.object,
  // values and options can have different structures. In that case, we need this to say two entities are equal.
  valueEqualsTreeItem: PropTypes.func,
  isTree: PropTypes.bool,
  onFocus: PropTypes.func,
  displaySpinner: PropTypes.bool,
  scrollIntoView: PropTypes.bool,
  autoFocus: PropTypes.bool,
};

export { Select };
export default Select;
